import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { allHistoriesKey, ss } from "consts";
import { HistoricModel } from "models";
import {
  AttBaseProviderType,
  PermissionAttProviderType,
  useAttendDetail,
} from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as AttendanceService from "services/attendance";
import { aytyFormatError } from "utils";

type HistoricContextType = {
  loading: boolean;
  histories?: HistoricModel[];
  getHistories: () => Promise<void>;
};

const HistoricContext = createContext({} as HistoricContextType);

const Provider = ({ children, all }: AttBaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [histories, setHistories] = useState<HistoricModel[]>();
  const { pIdOmni } = useAttendDetail();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (pIdOmni) {
      setLoading(true);
      setHistories(undefined);
    }
  }, [pIdOmni]);

  const getHistories = useCallback(async () => {
    let current: HistoricModel[] = all[pIdOmni];

    if (!current)
      await AttendanceService.getHistories({ pIdOmni })
        .then(async ({ data }) => {
          if (data.idReturnAPI > 0) {
            current = data.omniHistoryMessageList;
            all[pIdOmni] = data.omniHistoryMessageList;
          } else warning({ description: t(aytyFormatError(data)) });
        })
        .catch(errorsResolver.defaultError);

    setHistories(current);
    ss.setItem(allHistoriesKey, JSON.stringify(all));

    setLoading(false);
  }, [all, pIdOmni, errorsResolver, warning, t]);

  return (
    <HistoricContext.Provider value={{ loading, histories, getHistories }}>
      {children}
    </HistoricContext.Provider>
  );
};

export const HistoricProvider = ({
  hasPermission = true,
  ...args
}: PermissionAttProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useHistoric = () => useContext(HistoricContext);
